/**
 * Throwaway DataTable demo page — verification surface for the F5
 * primitives. Lives under /_dev/ so it ships with the bundle but
 * isn't linked from the sidebar. Sprint scope §4.5 requires this
 * proof-of-life before any retrofitted page consumes the primitive.
 *
 * Demonstrates:
 *   - Sort + multi-sort (Shift+click two headers).
 *   - Filter via global search.
 *   - Column visibility menu.
 *   - Column reorder (drag headers).
 *   - Pagination (3 rows here so all visible; bump `data.length` to
 *     test paging).
 *   - Bulk selection + bulk action slot.
 *   - Row click → EntityDrawer.
 *   - Excel + CSV export.
 *   - Skeleton loading.
 *   - EmptyState fallback.
 *
 * Remove the route in a post-M20 cleanup once every real page is on
 * DataTable.
 */

import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import {
  AIBadge,
  AutonomyBadge,
  DataTable,
  EntityDrawer,
  PageHeader,
  TraceableValue,
  WorkflowStateBadge,
} from "../../components/ui";

interface DemoRow {
  id: string;
  employee: string;
  type: string;
  start: string;
  end: string;
  days: number;
  state: string;
  outcome: "auto" | "drafted" | "escalated";
  ai_handled: boolean;
}

const SAMPLE: DemoRow[] = [
  {
    id: "r1",
    employee: "Ahmed Al-Otaibi",
    type: "Annual",
    start: "2026-06-05",
    end: "2026-06-10",
    days: 6,
    state: "pending_approval",
    outcome: "drafted",
    ai_handled: true,
  },
  {
    id: "r2",
    employee: "Nora Al-Saud",
    type: "Sick",
    start: "2026-05-12",
    end: "2026-05-13",
    days: 2,
    state: "approved",
    outcome: "auto",
    ai_handled: true,
  },
  {
    id: "r3",
    employee: "Mohamed Al-Rashid",
    type: "Hajj",
    start: "2026-07-15",
    end: "2026-07-25",
    days: 11,
    state: "denied",
    outcome: "escalated",
    ai_handled: true,
  },
  {
    id: "r4",
    employee: "Salma Al-Harbi",
    type: "Annual",
    start: "2026-08-01",
    end: "2026-08-07",
    days: 7,
    state: "approved",
    outcome: "auto",
    ai_handled: false,
  },
  {
    id: "r5",
    employee: "Rajesh Kumar",
    type: "Emergency",
    start: "2026-05-20",
    end: "2026-05-21",
    days: 2,
    state: "cancelled",
    outcome: "drafted",
    ai_handled: false,
  },
];

export function DataTableDemoPage() {
  const [drawerRow, setDrawerRow] = useState<DemoRow | null>(null);
  const [loading, setLoading] = useState(false);

  const columns = useMemo<ColumnDef<DemoRow>[]>(
    () => [
      {
        id: "employee",
        accessorKey: "employee",
        header: "Employee",
        cell: ({ row }) => (
          <span className="inline-flex items-center gap-1.5">
            {row.original.ai_handled && (
              <AIBadge tooltipText="Routed by AI" />
            )}
            {row.original.employee}
          </span>
        ),
      },
      {
        id: "type",
        accessorKey: "type",
        header: "Type",
      },
      {
        id: "start",
        accessorKey: "start",
        header: "Start",
      },
      {
        id: "end",
        accessorKey: "end",
        header: "End",
      },
      {
        id: "days",
        accessorKey: "days",
        header: "Days",
        cell: ({ row }) => (
          <TraceableValue
            onTraceClick={() => setDrawerRow(row.original)}
          >
            {row.original.days}
          </TraceableValue>
        ),
      },
      {
        id: "state",
        accessorKey: "state",
        header: "State",
        cell: ({ row }) => <WorkflowStateBadge state={row.original.state} />,
      },
      {
        id: "outcome",
        accessorKey: "outcome",
        header: "AI outcome",
        cell: ({ row }) =>
          row.original.ai_handled ? (
            <AutonomyBadge outcome={row.original.outcome} />
          ) : (
            <span className="text-xs text-text-tertiary">—</span>
          ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-5 p-5">
      <PageHeader
        title="DataTable demo"
        subtitle="Throwaway verification surface — F5 sprint scope §4.5"
        breadcrumbs={[
          { label: "Dev", href: "/_dev" },
          { label: "DataTable demo" },
        ]}
        actions={
          <button
            type="button"
            onClick={() => setLoading((l) => !l)}
            className="rounded-sm border border-border bg-surface-default px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary"
          >
            Toggle loading
          </button>
        }
      />

      <DataTable
        columns={columns}
        data={loading ? [] : SAMPLE}
        loading={loading}
        onRowClick={(row) => setDrawerRow(row)}
        renderBulkActions={(selected) => (
          <>
            <span className="text-xs">
              Demo bulk action: approve {selected.length} row(s)
            </span>
          </>
        )}
        exportFilename="datatable-demo"
      />

      <EntityDrawer
        open={drawerRow !== null}
        onOpenChange={(open) => !open && setDrawerRow(null)}
        title={
          drawerRow ? `${drawerRow.type} request` : "Request"
        }
        description={
          drawerRow
            ? `${drawerRow.employee} · ${drawerRow.start} → ${drawerRow.end}`
            : undefined
        }
        openInFullPageHref={drawerRow ? `/_dev/datatable-demo/${drawerRow.id}` : undefined}
      >
        {drawerRow && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-text-secondary">
              Days requested:{" "}
              <span className="font-mono">{drawerRow.days}</span>
            </p>
            <p className="text-sm text-text-secondary">
              State: <WorkflowStateBadge state={drawerRow.state} />
            </p>
            {drawerRow.ai_handled && (
              <p className="text-sm text-text-secondary">
                AI outcome: <AutonomyBadge outcome={drawerRow.outcome} />
              </p>
            )}
          </div>
        )}
      </EntityDrawer>
    </div>
  );
}
